import { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";
import { adminGetTrafficSummary, adminGetTrafficFunnel, adminGetProductPopularity, adminGetAbandonedCheckouts } from "@/lib/api";

/**
 * AdminTraffic — self-hosted visitor analytics dashboard.
 *
 * Sources every number from `GET /api/admin/analytics/summary?days=N`.
 * No third-party scripts, no cookies, no external calls — the entire
 * pipeline is your Mongo `page_views` collection populated by the
 * `useVisitorTracker` hook on the storefront.
 */

const RANGES = [
  { key: 1, label: "24h" },
  { key: 7, label: "7d" },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
];

const Funnel = ({ stages }) => {
  const top = stages.find((s) => s.count > 0)?.count || 0;
  const overall = stages.length
    ? (stages[stages.length - 1].count / (top || 1)) * 100
    : 0;
  return (
    <div className="bg-white border border-[#F1DCC5] rounded-2xl p-5" data-testid="admin-traffic-funnel">
      <div className="flex items-baseline justify-between mb-4">
        <div className="font-badge text-[10px] tracking-[0.2em] text-[#8A7568]">PURCHASE FUNNEL</div>
        <div className="text-xs text-[#8A7568]">
          Overall conversion:
          <span className="font-serif-display text-lg text-[#241C18] ml-2" data-testid="admin-funnel-overall">
            {top ? `${Math.round(overall * 10) / 10}%` : "—"}
          </span>
        </div>
      </div>
      {top === 0 ? (
        <div className="py-6 text-xs text-[#8A7568] text-center">
          Not enough traffic yet — as visitors browse, this funnel will fill in.
        </div>
      ) : (
        <div className="space-y-2">
          {stages.map((s, i) => {
            const widthPct = top ? Math.max(3, (s.count / top) * 100) : 0;
            const isDrop = i > 0 && s.pct_of_prev < 100 && stages[i - 1].count > 0;
            return (
              <div key={s.key} data-testid={`admin-funnel-stage-${s.key}`}>
                <div className="flex items-baseline justify-between text-xs mb-1">
                  <div className="font-badge tracking-[0.14em] uppercase text-[#241C18]">
                    {`${i + 1}. ${s.label}`}
                  </div>
                  <div className="text-[#8A7568]">
                    <span className="font-mono text-[#241C18] mr-2">{s.count}</span>
                    {i > 0 && stages[i - 1].count > 0 && (
                      <span className={isDrop ? "text-[#C97A63]" : ""}>
                        {`${s.pct_of_prev}% of prev`}
                      </span>
                    )}
                  </div>
                </div>
                <div className="relative h-6 bg-[#FFF3E6] rounded overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-[var(--gl-orange)] rounded transition-all"
                    style={{ width: `${widthPct}%` }}
                  />
                  {top > 0 && (
                    <div className="absolute inset-0 flex items-center justify-end pr-2 text-[10px] font-badge tracking-[0.14em] text-[#241C18]">
                      {`${s.pct_of_top}% of top`}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="text-[11px] text-[#8A7568] mt-4 leading-relaxed">
        Stages 1–4 count <em>distinct visitors</em> who reached that step in the selected window. Stage 5 counts <em>paid orders</em> (Stripe-confirmed) — so bots hitting the success page can't inflate it.
      </div>
    </div>
  );
};

const ProductPopularityTable = ({ title, rows, metric, emptyLabel, testid }) => (
  <div data-testid={testid} className="bg-white border border-[#F1DCC5] rounded-2xl overflow-hidden">
    <div className="px-5 py-3 border-b border-[#F1DCC5] flex items-baseline justify-between">
      <div className="font-badge text-[10px] tracking-[0.2em] text-[#8A7568]">{title}</div>
      <div className="font-badge text-[9px] tracking-[0.2em] text-[#8A7568]">{metric}</div>
    </div>
    {rows.length === 0 ? (
      <div className="p-6 text-center text-xs text-[#8A7568]">{emptyLabel}</div>
    ) : (
      <div className="divide-y divide-[#F1DCC5]">
        {rows.map((r, i) => (
          <Link
            key={r.slug}
            to={r.missing ? "#" : `/product/${r.slug}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`admin-popularity-row-${r.slug}`}
            className={`flex items-center gap-3 px-5 py-3 hover:bg-[#FFF8EE] transition ${r.missing ? "opacity-60" : ""}`}
          >
            <div className="font-serif-display text-lg text-[#241C18] w-6 text-right">
              {i + 1}
            </div>
            <div className="w-14 h-16 bg-[#FFE9D6] rounded overflow-hidden flex-shrink-0">
              {r.image && (
                <img src={r.image} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[#241C18] truncate">
                {r.name}
                {r.missing && <span className="ml-2 text-[10px] text-red-500">(deleted)</span>}
              </div>
              <div className="text-[11px] text-[#8A7568] flex gap-3 mt-0.5">
                {typeof r.price === "number" && <span>€{Number(r.price).toFixed(2)}</span>}
                {r.category && <span className="uppercase tracking-widest text-[9px]">{r.category}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="font-serif-display text-2xl text-[#241C18]">{r.visitors}</div>
              <div className="text-[10px] text-[#8A7568] uppercase tracking-widest">visitors</div>
            </div>
          </Link>
        ))}
      </div>
    )}
  </div>
);

const StatCard = ({ label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-[#F1DCC5] rounded-2xl p-5"
  >
    <div className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">
      {label}
    </div>
    <div className="font-serif-display text-4xl mt-1 text-[#241C18]">
      {value}
    </div>
    {sub && <div className="text-xs text-[#8A7568] mt-1">{sub}</div>}
  </div>
);

const Table = ({ title, rows, columns, testid, emptyLabel }) => (
  <div data-testid={testid} className="bg-white border border-[#F1DCC5] rounded-2xl overflow-hidden">
    <div className="px-5 py-3 border-b border-[#F1DCC5] font-badge text-[10px] tracking-[0.2em] text-[#8A7568]">
      {title}
    </div>
    {rows.length === 0 ? (
      <div className="p-6 text-center text-xs text-[#8A7568]">{emptyLabel || "No data yet."}</div>
    ) : (
      <div className="divide-y divide-[#F1DCC5]">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_80px_80px] items-center px-5 py-3 gap-3 text-sm">
            <div className="truncate text-[#241C18]" title={columns.label(r)}>{columns.label(r)}</div>
            <div className="text-right text-[#8A7568] text-xs">{r.visitors ?? "—"}<span className="ml-1">visitors</span></div>
            <div className="text-right text-[#241C18] font-mono">{r.pageviews}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const AbandonedCheckouts = ({ data }) => {
  const rows = data?.rows || [];
  const topProducts = data?.top_abandoned_products || [];
  const money = (n) => `€${Number(n || 0).toFixed(2)}`;
  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  return (
    <div className="bg-white border border-[#F1DCC5] rounded-2xl p-5" data-testid="admin-abandoned-checkouts">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <div>
          <div className="font-badge text-[10px] tracking-[0.2em] text-[#8A7568]">CHECKOUT DROP-OFF</div>
          <div className="font-serif-display text-xl mt-0.5 text-[#241C18]">
            {data.count} abandoned checkout{data.count === 1 ? "" : "s"} · <span className="text-[#8B1A1A]">{money(data.total_lost_amount)} in lost revenue</span>
          </div>
        </div>
        {topProducts.length > 0 && (
          <div className="text-xs text-[#8A7568]" data-testid="admin-abandoned-top-products">
            Most abandoned: {topProducts.map((p) => `${p.slug} (${p.abandons})`).join(", ")}
          </div>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-[#8A7568] py-6 text-center">
          Nothing abandoned in this window. Every shopper who started checkout completed it.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-badge tracking-[0.16em] text-[#8A7568]">
                <th className="text-left px-5 py-2 font-normal">WHEN</th>
                <th className="text-left px-5 py-2 font-normal">EMAIL</th>
                <th className="text-left px-5 py-2 font-normal">CART</th>
                <th className="text-right px-5 py-2 font-normal">AMOUNT</th>
                <th className="text-left px-5 py-2 font-normal">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 25).map((r, i) => (
                <tr
                  key={r.session_id || i}
                  data-testid={`admin-abandoned-row-${i}`}
                  className="border-t border-[#F1DCC5]"
                >
                  <td className="px-5 py-2.5 text-[#8A7568] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-5 py-2.5 text-[#241C18]">{r.email || <span className="text-[#8A7568] italic">guest — no email</span>}</td>
                  <td className="px-5 py-2.5">
                    <div className="flex items-center gap-2">
                      {(r.items || []).slice(0, 3).map((it, j) => (
                        it.image ? (
                          <img key={j} src={it.image} alt="" className="h-9 w-9 rounded object-cover" />
                        ) : (
                          <div key={j} className="h-9 w-9 rounded bg-[#F1DCC5]" />
                        )
                      ))}
                      {r.item_count > 3 && (
                        <div className="text-xs text-[#8A7568]">+{r.item_count - 3}</div>
                      )}
                    </div>
                    <div className="text-[11px] text-[#8A7568] mt-1 max-w-xs truncate">
                      {(r.items || []).map((it) => `${it.name}${it.variant_name ? ` (${it.variant_name})` : ""}`).join(" · ")}
                    </div>
                  </td>
                  <td className="px-5 py-2.5 text-right font-serif-display text-[#241C18]">{money(r.amount)}</td>
                  <td className="px-5 py-2.5 text-[11px] text-[#8A7568]">{r.payment_status || r.status || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 25 && (
            <div className="text-[11px] text-[#8A7568] text-center py-2">
              Showing 25 of {rows.length}. Widen the date range for older sessions.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminTraffic = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [popularity, setPopularity] = useState(null);
  const [abandoned, setAbandoned] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async (d) => {
    setLoading(true);
    setErr("");
    try {
      const [summary, fn, pop, ab] = await Promise.all([
        adminGetTrafficSummary(d),
        adminGetTrafficFunnel(d),
        adminGetProductPopularity(d),
        adminGetAbandonedCheckouts(d),
      ]);
      setData(summary);
      setFunnel(fn);
      setPopularity(pop);
      setAbandoned(ab);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  // Auto-refresh the live-visitors counter every 30s. Cheap — one small
  // aggregation query. We refresh the WHOLE summary so the live number
  // stays fresh without a separate endpoint.
  useEffect(() => {
    const t = setInterval(() => load(days), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const totals = data?.totals || { pageviews: 0, unique_visitors: 0, sessions: 0 };
  const device = data?.device || { mobile: 0, desktop: 0 };
  const totalDev = (device.mobile || 0) + (device.desktop || 0);
  const mobilePct = totalDev ? Math.round((device.mobile / totalDev) * 100) : 0;

  const chartData = useMemo(
    () => (data?.daily || []).map((r) => ({ date: r.date.slice(5), pageviews: r.pageviews, visitors: r.visitors })),
    [data?.daily]
  );

  return (
    <div className="p-6 md:p-10" data-testid="admin-traffic">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">TRAFFIC · SELF-HOSTED</div>
          <h1 className="font-serif-display text-3xl mt-1 text-[#241C18]">Visitor analytics</h1>
          <p className="text-xs text-[#8A7568] mt-1">
            Anonymous, cookie-free. Data lives in your own Mongo (auto-expires after 180 days). No third-party trackers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`admin-traffic-range-${r.key}`}
              onClick={() => setDays(r.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-badge tracking-[0.14em] uppercase ${
                days === r.key
                  ? "bg-[#241C18] text-[var(--gl-orange)]"
                  : "bg-white border border-[#F1DCC5] text-[#241C18]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data && <div className="text-sm text-[#8A7568]">Loading…</div>}
      {err && <div className="text-sm text-red-600 mb-4">{err}</div>}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard
          testid="admin-traffic-stat-live"
          label="LIVE (last 5 min)"
          value={<span className="inline-flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            {data?.live_visitors ?? 0}
          </span>}
          sub="unique visitors right now"
        />
        <StatCard testid="admin-traffic-stat-visitors" label="Unique visitors" value={totals.unique_visitors} sub={`in the last ${days === 1 ? "24 hours" : `${days} days`}`} />
        <StatCard testid="admin-traffic-stat-pageviews" label="Pageviews" value={totals.pageviews} sub={totals.sessions ? `${totals.sessions} sessions` : ""} />
        <StatCard testid="admin-traffic-stat-mobile" label="Mobile share" value={`${mobilePct}%`} sub={`${device.mobile} mobile · ${device.desktop} desktop`} />
      </div>

      {/* Daily chart */}
      <div className="bg-white border border-[#F1DCC5] rounded-2xl p-5 mb-6" data-testid="admin-traffic-chart">
        <div className="font-badge text-[10px] tracking-[0.2em] text-[#8A7568] mb-3">DAILY PAGEVIEWS</div>
        {chartData.length === 0 ? (
          <div className="h-56 flex items-center justify-center text-xs text-[#8A7568]">
            No visitors yet — the moment someone lands on the site, they'll show up here.
          </div>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: -20 }}>
                <CartesianGrid stroke="#F1DCC5" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke="#8A7568" fontSize={11} />
                <YAxis stroke="#8A7568" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#241C18", color: "white", border: "none", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#F4CB47" }}
                  itemStyle={{ color: "#F9F8F5" }}
                />
                <Line type="monotone" dataKey="pageviews" stroke="#F4CB47" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="visitors" stroke="#C97A63" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Funnel */}
      {funnel && <div className="mb-6"><Funnel stages={funnel.stages} /></div>}

      {/* Abandoned checkouts — carts that started Stripe checkout but
          never paid. The #1 tool for spotting "why is my conversion low?" */}
      {abandoned && <div className="mb-6"><AbandonedCheckouts data={abandoned} /></div>}

      {/* Product popularity */}
      {popularity && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
          <ProductPopularityTable
            testid="admin-popularity-viewed"
            title="MOST-VIEWED PRODUCTS"
            metric="Unique visitors"
            rows={popularity.most_viewed || []}
            emptyLabel="No product views yet."
          />
          <ProductPopularityTable
            testid="admin-popularity-cart"
            title="MOST ADDED-TO-CART"
            metric="Unique visitors"
            rows={popularity.most_added_to_cart || []}
            emptyLabel="Nobody's added a product to cart yet — as soon as they do, the leaders show up here."
          />
        </div>
      )}

      {/* Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Table
          testid="admin-traffic-top-pages"
          title="TOP PAGES"
          rows={data?.top_paths || []}
          emptyLabel="No pageviews yet."
          columns={{ label: (r) => r.path }}
        />
        <Table
          testid="admin-traffic-top-referrers"
          title="TOP REFERRERS"
          rows={data?.top_referrers || []}
          emptyLabel="Nobody's referred a visit yet — share your Instagram bio link!"
          columns={{ label: (r) => r.referrer }}
        />
      </div>

      {/* Browsers */}
      {(data?.browsers || []).length > 0 && (
        <div className="mt-6 bg-white border border-[#F1DCC5] rounded-2xl p-5" data-testid="admin-traffic-browsers">
          <div className="font-badge text-[10px] tracking-[0.2em] text-[#8A7568] mb-3">BROWSERS</div>
          <div className="flex flex-wrap gap-2">
            {data.browsers.map((b) => (
              <span key={b.browser} className="px-3 py-1.5 rounded-full border border-[#F1DCC5] text-xs">
                <span className="font-badge tracking-[0.1em]">{b.browser}</span>
                <span className="text-[#8A7568] ml-2">{b.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTraffic;
